import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { getStoredConsent, setStoredConsent, type ConsentStatus } from '../../analytics/cookieConsent'
import { disableGoogleAnalytics, loadGoogleAnalytics } from '../../analytics/googleAnalytics'
import { track } from '../../analytics/track'
import { CookieConsentBanner } from './CookieConsentBanner'

interface CookieConsentValue {
  status: ConsentStatus | null
  bannerOpen: boolean
  accept: () => void
  decline: () => void
  /** Reopens the banner so a visitor can change a decision already made - see the
   *  "Cookie-Einstellungen" link in LegalLinks.tsx, present on every page (marketing and app). */
  reopen: () => void
}

// Inert default (no-op reopen, banner never shown) rather than throwing outside a provider -
// LegalLinks (which calls useCookieConsent for its "Cookie-Einstellungen" button) is rendered by
// almost every marketing page, and most of their unit tests render just that one page in
// isolation without caring about cookie consent at all. Only App.tsx's real CookieConsentProvider
// makes the button/banner actually do anything; everywhere else it degrades to a harmless no-op.
const noop = () => {}
const CookieConsentContext = createContext<CookieConsentValue>({
  status: null,
  bannerOpen: false,
  accept: noop,
  decline: noop,
  reopen: noop,
})

/** Wraps the whole app (see App.tsx) so the banner/consent state is available and persists across
 *  client-side navigation, not tied to any one page. Google Analytics is only ever loaded here,
 *  after consent - never unconditionally in index.html like the cookieless Umami tracker (see
 *  ANALYTICS.md and content/datenschutzerklaerung.ts section 11). */
export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ConsentStatus | null>(null)
  const [bannerOpen, setBannerOpen] = useState(false)

  useEffect(() => {
    const stored = getStoredConsent()
    setStatus(stored)
    setBannerOpen(stored === null)
    if (stored === 'granted') loadGoogleAnalytics()
  }, [])

  function accept() {
    setStoredConsent('granted')
    setStatus('granted')
    setBannerOpen(false)
    loadGoogleAnalytics()
    track('cookie_consent_decided', { choice: 'granted' })
  }

  function decline() {
    setStoredConsent('denied')
    setStatus('denied')
    setBannerOpen(false)
    disableGoogleAnalytics()
    track('cookie_consent_decided', { choice: 'denied' })
  }

  function reopen() {
    setBannerOpen(true)
  }

  return (
    <CookieConsentContext.Provider value={{ status, bannerOpen, accept, decline, reopen }}>
      {children}
      <CookieConsentBanner open={bannerOpen} onAccept={accept} onDecline={decline} />
    </CookieConsentContext.Provider>
  )
}

export function useCookieConsent(): CookieConsentValue {
  return useContext(CookieConsentContext)
}
